/**
 * Saying a word out loud.
 *
 * Uses the browser's own voice: free, offline, no key, works today. Google
 * Text-to-Speech goes behind this same function later — everything that speaks
 * calls `say()` and nothing else knows which engine is running.
 */

import { music } from './sound'

let warned = false

export function canSpeak(): boolean {
  return typeof window !== 'undefined' && 'speechSynthesis' in window
}

/** What a spoken word did, for a screen that must not depend on hearing it. */
export type SpeechState = 'speaking' | 'done' | 'failed'

// If the voice has not started this long after asking, treat it as failed.
// An iPad that has been in the background can leave the voice stuck: it takes
// the request and never speaks (build list item 2, 25 Oct 2026).
const START_WITHIN_MS = 2500

// Each say() is one attempt, and a newer attempt for the same listener
// replaces the older one. A stuck first try's timer used to fire after a
// replay had started, so the screen said "failed" and showed the spelling
// clue while the word was playing (Codex review, 27 Oct 2026). A word cut off
// by someone else's say() did end, so that listener hears "done".
let latest = 0
let latestListener: ((s: SpeechState) => void) | undefined

export function say(text: string, lang = 'en-US', rate = 0.9, onState?: (s: SpeechState) => void): void {
  if (!text) return
  if (!canSpeak()) {
    // A missing voice must never block the session. The word is on screen.
    if (!warned) {
      console.warn('This browser has no speech synthesis; words will be shown, not spoken.')
      warned = true
    }
    onState?.('failed')
    return
  }
  const attempt = ++latest
  latestListener = onState
  const current = () => attempt === latest
  const report = (s: SpeechState) => {
    if (current()) onState?.(s)
    else if (onState !== latestListener) onState?.('done')
  }
  try {
    window.speechSynthesis.cancel()
    // A paused voice (after the app was in the background) ignores speak().
    window.speechSynthesis.resume()
    const utterance = new SpeechSynthesisUtterance(text)
    utterance.lang = lang
    utterance.rate = rate
    // Music under a spoken word is the one place it would get in the way of
    // learning, so it drops almost to nothing until the voice finishes.
    music.duck(true)
    // Only the newest attempt owns the music; an old one ending must not
    // bring it back up under the word that replaced it.
    const restore = () => { if (current()) music.duck(false) }
    let started = false
    let over = false
    const finish = (s: SpeechState) => {
      if (over) return
      over = true
      restore()
      report(s)
    }
    utterance.onstart = () => {
      // A newer word has taken over: this one must not touch the screen or the music.
      if (!current()) return
      // A slow first voice can start after we gave up on it: it is speaking after all.
      if (over) { over = false; music.duck(true) }
      started = true
      report('speaking')
    }
    utterance.onend = () => finish('done')
    // Replay cancels the word before it; that is not a failure.
    utterance.onerror = (e) => finish(e.error === 'interrupted' || e.error === 'canceled' ? 'done' : 'failed')
    window.setTimeout(() => { if (!started) finish('failed') }, START_WITHIN_MS)
    // Some browsers never fire onend for a cancelled utterance. Restore anyway
    // after a generous allowance, so the music cannot be left ducked for ever.
    window.setTimeout(() => finish('done'), START_WITHIN_MS + 1500 + text.length * 120)
    window.speechSynthesis.speak(utterance)
  } catch {
    // Same rule: never let the voice take the session down with it.
    music.duck(false)
    report('failed')
  }
}

// Coming back to the app is when an iPad's voice gets stuck, so clear it then.
if (typeof document !== 'undefined' && canSpeak()) {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      try { window.speechSynthesis.cancel() } catch { /* nothing to clear */ }
    }
  })
}

/**
 * The first-language word, spoken in that language.
 *
 * Decided earlier: pressing the 中文 button speaks the WORD in the child's own
 * language, not a whole sentence.
 */
export function sayInFirstLanguage(text: string, firstLanguage: string): void {
  const langMap: Record<string, string> = {
    'zh-TW': 'zh-TW', 'zh-CN': 'zh-CN', 'zh': 'zh-TW',
    'es': 'es-ES', 'fr': 'fr-FR', 'ko': 'ko-KR', 'ja': 'ja-JP',
  }
  say(text, langMap[firstLanguage] ?? firstLanguage, 0.85)
}
